import React, { useEffect } from "react";

import Text from "src/components/text";
import SeekComponent from "./components/seek";

import { fetchMicroLearnings } from "src/redux/actions/coaching/microLearning";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useSelector } from "react-redux";
import { MicroLearning } from "src/types/coaching/microLearning";

import InfiniteScroll from "src/components/infiniteScroll";
import TimeAgo from "react-timeago";

import _ from "lodash";
import cx from "classnames";
import styles from "./microLearning.module.scss";

const MicroLearningPage: React.FC<object> = () => {
  const dispatch = useDispatch();

  const { loading, loaded, learnings, hasMoreItems, nextPage, currentPage } =
    useSelector((state: any) => {
      const microLearningState = _.get(state.coaching, `microLearnings`);
      const loading = _.get(microLearningState, "loading");
      const learnings = _.get(microLearningState, "items");
      const loaded = _.get(microLearningState, "loaded", false);

      const pagination = _.get(microLearningState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");

      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }

      return {
        loading,
        loaded,
        learnings,
        totalRecordsCount,
        currentPage,
        total,
        nextPage,
        hasMoreItems,
      };
    }, _.isEqual);

  const handleLoadMore = () => {
    if (nextPage) {
      dispatch(
        fetchMicroLearnings({
          page: nextPage,
          limit: 10,
        }),
      );
    }
  };

  useEffect(() => {
    if (_.size(learnings) > 0) {
      return;
    }

    dispatch(
      fetchMicroLearnings({
        page: 0,
        limit: 10,
      }),
    );
  }, []);

  return (
    <section className={styles.container}>
      <div className={styles.top}>
        <SeekComponent />
      </div>
      <div className={styles.bottom}>
        <div className={styles.wrap}>
          <InfiniteScroll
            className={styles.list}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadMore}
            loader={() => {
              return _.times(
                _.size(learnings) === 0 || currentPage === 0 ? 7 : 1,
                (index) => <MicroLearningPlaceholder key={index} />,
              );
            }}
          >
            <div className={styles.learningContainer}>
              {(learnings || []).map((item: MicroLearning, index: number) => {
                return <MicroLearningItem key={item.id} microLearning={item} index={index} />;
              })}
            </div>
          </InfiniteScroll>
        </div>
      </div>
    </section>
  );
};

const MicroLearningItem = ({ microLearning, index }) => {
  const navigate = useNavigate();

  const modifiedDate = _.get(microLearning, "modifiedDate", null);

  const handleClick = (item: MicroLearning) => {
    navigate(`/coaching/microlearning/${item.id}`);
  };

  const podcastType = "discovery";
  const rotation = `rotate-${index % 5}`;

  const modalities = _.get(microLearning, "modalities", []);

  return (
    <div
      key={microLearning.id}
      className={styles.wrapper}
      onClick={() => handleClick(microLearning)}
    >
      <div className={styles.metadata}>
        <div className={styles.pill}>
          {modifiedDate > 0 ? (
            <TimeAgo date={parseInt(modifiedDate)} />
          ) : (
            "few seconds ago" 
          )}
        </div>
        {modalities.filter((modality) => modality === 'article' || modality === 'podcast').map((modality) => (
          <div className={styles.pill}>{modality}</div>
        ))}
      </div>
      <div key={microLearning.id} className={styles.item}>
        <div className={styles.left}>
          <h3 className={styles.name}>{microLearning.title}</h3>
          <Text
            lines={2}
            className={styles.description}
            text={microLearning.description}
          />
        </div>
        <div className={cx(styles.right, styles[rotation])}>
          <img
            src="/images/assets/courseThumbnail.jpg"
            alt={microLearning.title}
            className={styles.course}
          />
          <div className={styles.thumbnails}>
            <img
              src="/images/assets/podcastThumbnail.jpg"
              alt={microLearning.title}
              className={styles.podcast}
            />
            <img
              src="/images/assets/simulationThumbnail.jpg"
              alt={microLearning.title}
              className={cx(
                styles.simulation,
                podcastType === "discovery"
                  ? styles.discovery
                  : styles.coldcall,
              )}
            />
          </div>
        </div>
      </div>
      <div className={styles.divider}></div>
    </div>
  );
};

const MicroLearningPlaceholder = () => {
  return (
    <div
      className={cx(styles.learningPlaceholderContainer, styles.placeholder)}
    >
      {_.times(3, (index) => (
        <div key={index} className={cx(styles.placeholderwrapper)}>
          <div className={styles.metadata}>
            <div className={cx(styles.block, styles.pill)} />
            <div className={cx(styles.block, styles.pill)} />
          </div>

          <div className={styles.container}>
            <div className={styles.left}>
              <div className={cx(styles.block, styles.title)} />
              <div className={cx(styles.block, styles.line)} />
              <div className={cx(styles.block, styles.lineShort)} />
            </div>

            <div className={cx(styles.right, styles[`rotate-${index % 5}`])}>
              <div className={cx(styles.block, styles.course)} />
              <div className={cx(styles.thumbnails)}>
                <div className={cx(styles.block, styles.thumb)} />
                <div className={cx(styles.block, styles.thumb)} />
              </div>
            </div>
          </div>

          <div className={styles.divider} />
        </div>
      ))}
    </div>
  );
};

export default MicroLearningPage;
